import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  getCurrentUserId,
  getUserFullName,
  logout,
  getUserProfile,
} from "../../utils/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faCommentDots,
  faUser,
  faSignOutAlt,
  faIndianRupeeSign,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Icon } from "@iconify/react";
import { notificationApi } from "../../api/notificationApi";
import { walletApi } from "../../api/walletApi";
import api from "../../api/axios";
import SearchUserProfile from "../ui/SearchBar/SearchUserProfile";
import Notification from "../ui/Notification/Notification";
import { useProfile } from "../../hook/useProfile";
import logo1 from "../../assets/images/logo1.png";
import ProfileBadge from "./ProfileBadge";
import { getSocket } from "../../socket/Socket";
import { toast } from "react-toastify";

const TopHeader = ({ onToggleSidebar, creditAmount = 0 }) => {
  const navigate = useNavigate();
  const currentUserId = getCurrentUserId();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [isActiveSearchBar, setIsActiveSearchBar] = useState(false);
  const [isResponsive, setIsResponsive] = useState(false);
  const { isProfileInfo, isLoading, updateProfile } = useProfile();
  const isBatchCheck = isProfileInfo?.isBatch;
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef(null);
  const notification = useRef(null);
  const [isSearch, setIsSearch] = useState("");
  const [isNotification, setIsNotification] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [notificationCounts, setNotificationCounts] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const lastApiCountRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const debounceTimer = useRef(null);
  const location = useLocation();

  // ===== FOR MESSAGE COUNTER ===== //

  const lastMessageCountRef = useRef(0);
  const walletLink = "/wallet";
  const profileLink = `/my/${currentUserId}/profile`;
  const seenKey = `seen_notification_count_${currentUserId}`;

  // closer function

  // const closeAllPopups = () => {
  //   setIsNotification(false);
  //   setShowProfile(false);
  //   setIsSearch("");
  //    setDebouncedSearch(""); 
  // };

  const closeAllPopups = () => {
  setIsNotification(false);
  setShowProfile(false);

  // ===== SEARCH RESET =====
  setIsSearch("");
  setDebouncedSearch("");
  setActiveIndex(0);
  setShowSearchResults(false);

  if (debounceTimer.current) {
    clearTimeout(debounceTimer.current);
  }
};

useEffect(() => {
  setIsSearch("");
  setDebouncedSearch("");
  setActiveIndex(0);
  setShowSearchResults(false);

  if (debounceTimer.current) {
    clearTimeout(debounceTimer.current);
  }
}, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
      if (notification.current && !notification.current.contains(e.target)) {
        setIsNotification(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
        toast.success("Logged out successfully");
        navigate("/login");
      }
    });
  };

  const fetchWallet = async () => {
    try {
      const data = await walletApi.getWallet();
      if (data?.success) setWallet(data);
    } catch {}
  };

  useEffect(() => {
    fetchWallet();
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const data = await notificationApi.getNotifications();
      const apiCount = data?.count || 0;
      lastApiCountRef.current = apiCount;

      const storedCount = Number(localStorage.getItem(seenKey) || 0);

      if (apiCount > storedCount) {
        setNotificationCounts(apiCount - storedCount);
      } else {
        setNotificationCounts(0);
      }
    } catch {
      console.log("somethink went wrong");
    }
  };

  useEffect(() => {
    fetchNotificationCount();
  }, []);

  // FOR MESSAGE COUNTER

  useEffect(() => {
    const total = Number(localStorage.getItem("total_message") || 0);
    lastMessageCountRef.current = total;
    if (total) {
      setMessageCount(total);
    } else {
      setMessageCount(0);
    }
  }, []);

  const handleNotificationClick = () => {
    const wasOpen = isNotification;
    closeAllPopups();

    if (wasOpen) {
      setIsNotification(false);
      setNotificationCounts(0);
    } else {
      localStorage.setItem(seenKey, lastApiCountRef.current);
      setNotificationCounts(0);
      setIsNotification(true);
    }
  };

  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // for notification
    const handleNotification = (data, type) => {
      // console.log("New Notification:", data?.from);

      setNotifications((prev) => [
        {
          // _id: Date.now(),
          _id: data?.from,
          type,
          createdAt: new Date(),
          isRead: false,
          from: {
            _id: data?.from?._id,
            fullName: data?.from?.fullName,
            profilePic: data?.from?.profilePic,
          },
        },
        ...prev,
      ]);

      // Increase bell count instantly
      setNotificationCounts((prev) => prev + 1);
    };

    socket.on("followed", (data) => {
      handleNotification(data, "FOLLOWED");
    });

    socket.on("follow_request", (data) => {
      handleNotification(data, "FOLLOW_REQUEST");
    });

    socket.on("follow_request_accepted", (data) => {
      handleNotification(data, "FOLLOW_ACCEPTED");
    });

    socket.on("post_liked", (data) => {
      handleNotification(data, "POST_LIKED");
    });

    socket.on("comment_replied", (data) =>
      handleNotification(data, "COMMENT_REPLIED"),
    );

    socket.on("post_commented", (data) =>
      handleNotification(data, "POST_COMMENTED"),
    );

    socket.on("post_reposted", (data) =>
      handleNotification(data, "POST_REPOSTED"),
    );

    socket.on("new_message_request", (data) => {
      handleNotification(data, "MESSAGE_REQUEST_SENT");
    });

    socket.on("message_request_accepted", (data) => {
      handleNotification(data, "MESSAGE_REQUEST_ACCEPTED");
    });

    socket.on("revolution_supported", (data) => {
      handleNotification(data, "SUPPORTED_REVOLUTION");
    });

    socket.on("comment_liked", (data) => {
      handleNotification(data, "COMMENT_LIKED");
    });

    // for message
    socket.on("new_message", (data) => {
      setMessageCount((prev) => {
        const newCount = prev + 1;
        localStorage.setItem("total_message", newCount);
        return newCount;
      });
    });

    return () => {
      socket.off("followed");
      socket.off("follow_request");
      socket.off("follow_request_accepted");
      socket.off("post_liked");
      socket.off("comment_replied");
      socket.off("post_commented");
      socket.off("post_reposted");
      socket.off("new_message_request");
      socket.off("revolution_supported");
      socket.off("message_request_accepted");
      socket.off("comment_liked");
    };
  }, []);

  const [isCheckBadge, setCheckBadge] = useState();

  useEffect(() => {
    const fetchMyProfile = async () => {
      try {
        const res = await api.get("/user/me");
        setCheckBadge(res?.data);
      } catch (err) {
        console.error("Profile fetch error", err);
      }
    };
    fetchMyProfile();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 576) {
        setIsResponsive(true);
      } else {
        setIsResponsive(false);
        setIsActiveSearchBar(false);
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="feed-page-top-container">
      <nav className="navbar feed-top-header px-3 px-md-5">
        <div className="logo d-flex me-2" onClick={onToggleSidebar}>
          <img src={logo1} alt="logo" />
          <span className="bharat-jagriti-text d-none d-md-block">
            Bharat Jagriti
          </span>
        </div>

        {/* <div className="search-user-profile-container position-md-relative">
          <div
            className={`search-box ${isActiveSearchBar && isResponsive ? "" : "d-none"} d-sm-flex`}
          >
            <i className="prime-search-icon">
              <Icon icon="ic:sharp-search" />
            </i>
            <input
              value={isSearch}
              onChange={(e) => {
                const rawValue = e.target.value;
                const filteredValue = rawValue.replace(/[^a-zA-Z\s0-9]/g, "");
                setIsSearch(filteredValue);
                setActiveIndex(0);

                // ===== DEBOUNCE LOGIC =====
                if (debounceTimer.current) {
                  clearTimeout(debounceTimer.current);
                }
                debounceTimer.current = setTimeout(() => {
                  setDebouncedSearch(filteredValue);
                }, 550); // 550ms delay
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActiveIndex((prev) => prev + 1);
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
                }
                // Allow letters, spaces, numbers and control keys
                const isInvalidKey =
                  /[^a-zA-Z\s0-9]/.test(e.key) &&
                  !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key);
                if (isInvalidKey) {
                  e.preventDefault();
                }
              }}
              
              type="text"
              placeholder="Search by name or phone number..."
            />
          </div>

          {debouncedSearch.length > 0 && isActiveSearchBar && (
            <div className="search-user-profile-wrapper">
              <SearchUserProfile
                SearchData={debouncedSearch}
                onClose={() => {
                  setIsSearch("");
                  setDebouncedSearch("");
                  setActiveIndex(0);

                  if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                  }
                }}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                onSelectUser={() => {
                  // ===== FULL RESET =====
                  setIsSearch("");
                  setDebouncedSearch("");
                  setActiveIndex(0);

                  if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                  }
                }}
              />
            </div>
          )}
        </div> */}

        <div className="search-user-profile-container position-md-relative">
          <div
            className={`search-box ${
              isActiveSearchBar && isResponsive ? "" : "d-none"
            } d-sm-flex`}
          >
            <i className="prime-search-icon">
              <Icon icon="ic:sharp-search" />
            </i>

            <input
              value={isSearch}
              onChange={(e) => {

                if (e.nativeEvent.inputType === undefined && !e.nativeEvent.data) return;

                const rawValue = e.target.value;

                const filteredValue = rawValue.replace(
                  /[^a-zA-Z\s0-9]/g,
                  ""
                );

                setIsSearch(filteredValue);
                setActiveIndex(0);

                // ===== EMPTY SEARCH =====
                if (!filteredValue.trim()) {
                  setDebouncedSearch("");
                  setShowSearchResults(false);

                  if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                  }

                  return;
                }

                // ===== OPEN DROPDOWN =====
                setShowSearchResults(true);

                // ===== DEBOUNCE =====
                if (debounceTimer.current) {
                  clearTimeout(debounceTimer.current);
                }

                debounceTimer.current = setTimeout(() => {
                  setDebouncedSearch(filteredValue.trim());
                }, 550);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();

                  setActiveIndex((prev) => prev + 1);
                }

                if (e.key === "ArrowUp") {
                  e.preventDefault();

                  setActiveIndex((prev) =>
                    prev > 0 ? prev - 1 : 0
                  );
                }

                // ===== BLOCK SPECIAL CHARS =====
                const isInvalidKey =
                  /[^a-zA-Z\s0-9]/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                  ].includes(e.key);

                if (isInvalidKey) {
                  e.preventDefault();
                }
              }}
              type="text"
              placeholder="Search by name or phone number..."
            />
          </div>

          {/* ===== SEARCH RESULTS ===== */}
          {showSearchResults &&
            debouncedSearch.length > 0 && (
              <div className="search-user-profile-wrapper">
                <SearchUserProfile
                  SearchData={debouncedSearch}
                  onClose={() => {
                    setIsSearch("");
                    setDebouncedSearch("");
                    setActiveIndex(0);
                    setShowSearchResults(false);

                    if (debounceTimer.current) {
                      clearTimeout(debounceTimer.current);
                    }
                  }}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  onSelectUser={() => {
                    // ===== COMPLETE RESET =====
                    setIsSearch("");
                    setDebouncedSearch("");
                    setActiveIndex(0);
                    setShowSearchResults(false);

                    if (debounceTimer.current) {
                      clearTimeout(debounceTimer.current);
                    }
                  }}
                />
              </div>
            )}
          </div>

        <div className="global-icons d-flex gap-0 gap-md-2">
          {isResponsive && (
            <span className="position-relative border-0">
              <FontAwesomeIcon
                onClick={() => setIsActiveSearchBar((prev) => !prev)}
                icon={isActiveSearchBar ? faXmark : faMagnifyingGlass}
                className="top_header_search_icon"
              />
            </span>
          )}

          {!isActiveSearchBar && (
            <Link to={walletLink} className="post-header-link">
              <span className="position-relative border-0">
                <FontAwesomeIcon
                  icon={faIndianRupeeSign}
                  className="icon-box"
                />
                {creditAmount > 0 && (
                  <span className="global-creditCard-data position-absolute translate-middle badge rounded-pill bg-danger">
                    <Icon icon="ic:sharp-currency-rupee" /> {wallet.balance}
                  </span>
                )}
              </span>
            </Link>
          )}

          {!isActiveSearchBar && (
            <span
              className="position-relative border-0"
              ref={notification}
              onClick={handleNotificationClick}
            >
              <FontAwesomeIcon icon={faBell} className="icon-box" />
              {notificationCounts > 0 && (
                <span className="global-bell-data position-absolute translate-middle badge rounded-pill bg-danger">
                  {notificationCounts}
                </span>
              )}
              {isNotification && (
                <div className="notification-wrapper">
                  <Notification />
                </div>
              )}
            </span>
          )}

          {!isActiveSearchBar && (
            <span
              className="position-relative border-0"
              onClick={() => {
                closeAllPopups();

                localStorage.removeItem("total_message");
                setMessageCount(0);

                navigate("/message");
              }}
            >
              <FontAwesomeIcon icon={faCommentDots} className="icon-box" />
              {messageCount > 0 && (
                <span className="global-comment-data position-absolute translate-middle badge rounded-pill bg-danger">
                  {messageCount}
                </span>
              )}
            </span>
          )}

          {!isActiveSearchBar && (
            <span className="position-relative border-0" ref={profileRef}>
              <FontAwesomeIcon
                icon={faUser}
                className="icon-box profile-icon"
                onClick={() => {
                  closeAllPopups();
                  setShowProfile(!showProfile);
                }}
              />

              {showProfile && (
                <div className="profile-popup">
                  <div className="profile-card">
                    <div className="profile-header">
                      <ProfileBadge
                        isBatch={isBatchCheck}
                        ProfileLink={profileLink}
                        profilePic={isProfileInfo?.profilePic}
                      />

                      <div className="d-flex flex-column">
                        <div className="d-flex">
                          <Link
                            to={profileLink}
                            className="post-header-link text-decoration-none text-dark"
                          >
                            <span className="username">
                              {isProfileInfo?.fullName}
                            </span>
                          </Link>

                          {/* ======= VERIFICATION ICON START ======= */}
                          {(isProfileInfo?.role === "PRIME" ||
                            isProfileInfo?.role === "PUBLIC") && (
                            isProfileInfo?.adhaarVerified === "VERIFIED" ? (
                              <span
                                className={`ms-1 ${
                                  isProfileInfo?.role === "PRIME"
                                    ? "feed-verified-badge-success" // green
                                    : "feed-verified-badge" // blue
                                }`}
                              >
                                <Icon icon="ic:baseline-verified" />
                              </span>
                            ) : (
                              <span className="feed-verified-badge ms-1">
                                <Icon
                                  icon="ic:sharp-warning-amber"
                                  className="text-warning"
                                />
                              </span>
                            )
                          )}
                          {/* ======= VERIFICATION ICON END ======= */}
                        </div>

                        <span
                          className={`user-verify-text ${
                            isProfileInfo?.adhaarVerified === "VERIFIED"
                              ? "theme-color"
                              : "text-danger"
                          }`}
                          onClick={() => {
                            if (isProfileInfo?.adhaarVerified !== "VERIFIED") {
                              navigate("/aadhaar-verification");
                            }
                          }}
                        >
                          {isProfileInfo?.adhaarVerified === "VERIFIED"
                            ? "Verified"
                            : "Verify"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="create-post-btn text-center"
                    onClick={handleLogout}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </span>
          )}
        </div>
      </nav>
    </div>
  );
};

export default TopHeader;
